import React, { useState, useEffect } from 'react';
import { X, Send, Calendar, Loader2, CheckCircle, AlertCircle, Clock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_SUBJECTS = {
  1:  'Email 1: Where does your cultural fluency begin?',
  2:  'Email 2: Your normal may not be universal',
  3:  'Email 3: Culture is operating even when nobody names it',
  4:  'Email 4: Did your message land as intended?',
  5:  'Email 5: Capability is not always received as capability',
  6:  'Email 6: Adapting does not mean disappearing',
  7:  'Email 7: Every room has a language',
  8:  'Email 8: Do not leave your team\'s culture to chance',
  9:  'Email 9: Clarity without humiliation',
  10: 'Email 10: Diversity is the beginning, not the destination',
  11: 'Email 11: Know what must not move',
  12: 'Email 12: What if their logic makes sense from where they stand?',
  13: 'Email 13: Translate the message, not your identity',
  14: 'Email 14: Build a third space',
  15: 'Email 15: Cultural fluency is never finished',
  16: 'Email 16: Respect Has More Than One Accent',
  17: 'Email 17: Before You Decide Someone Is Difficult',
  18: 'Email 18: The Meeting Has a Culture Too',
  19: 'Email 19: Who Actually Gets to Decide?',
  20: 'Email 20: Feedback Travels Through Culture',
  21: 'Email 21: The Extra Work Nobody Sees',
  22: 'Email 22: When Culture Fit Really Means Familiarity',
  23: 'Email 23: Trust Does Not Start in the Same Place',
  24: 'Email 24: Your "Common Sense" Had to Be Learned Somewhere',
  25: 'Email 25: Who Is Doing All the Adapting?',
  26: 'Email 26: Belonging Begins After the Welcome',
  27: 'Email 27: The Cost of Getting Silence Wrong',
  28: 'Email 28: Directness Is a Dial, Not a Switch',
  29: 'Email 29: The Person With the Best English May Not Have the Best Idea',
  30: 'Email 30: Who Speaks Last Matters',
  31: 'Email 31: Dignity Changes the Conversation',
  32: 'Email 32: Can Your Team Disagree Well?',
  33: 'Email 33: One Cultural Habit Worth Watching This Month',
  34: 'Email 34: The Leadership Cost of Being Too Certain',
  35: 'Email 35: When Warmth Looks Unprofessional',
  36: 'Email 36: When Distance Looks Uncaring',
  37: 'Email 37: Power Is Present Even When Nobody Mentions It',
  38: 'Email 38: The Question Behind the Question',
  39: 'Email 39: You Can Be Right and Still Lose the Room',
  40: 'Email 40: Cultural Fluency Is Built in Small Moments',
  41: 'Email 41: What Happens After the Misunderstanding?',
  42: 'Email 42: Remote Work Still Has a Culture',
  43: 'Email 43: A Shared Culture Should Be Built Deliberately',
  44: 'Email 44: What Does Your Team Reward?',
  45: 'Email 45: Confidence Has Cultural Accents',
  46: 'Email 46: What Are You Asking People to Leave at the Door?',
  47: 'Email 47: Four Questions for a Difficult Room',
  48: 'Email 48: Revisit Your Cultural Fluency',
  49: 'Email 49: What Have You Started Seeing Differently?',
  50: 'Email 50: Keep Becoming Fluent (sequence complete)',
};

const getCsrf = () => {
  const m = document.cookie.split('; ').find(r => r.startsWith('tcl_csrf='));
  return m ? m.split('=')[1] : '';
};

// Returns a datetime-local string defaulting to 2 hours from now
const defaultScheduleTime = () => {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
};

export default function SubscriberSendModal({ subscriber, onClose, onSuccess }) {
  const [stage, setStage] = useState(
    subscriber.nurture_stage > 0 ? Math.min(subscriber.nurture_stage + 1, 50) : 1
  );
  const [mode, setMode] = useState('now'); // 'now' | 'schedule'
  const [scheduleAt, setScheduleAt] = useState(defaultScheduleTime);
  const [overrideStage, setOverrideStage] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!showPreview) return;
    setPreviewLoading(true);
    setPreviewHtml('');
    fetch(
      `${API_URL}/api/cfq/nurture/preview/${stage}?cfq_completed=${subscriber.cfq_completed || false}`,
      { headers: authHeaders, credentials: 'include' }
    )
      .then(r => r.ok ? r.text() : null)
      .then(html => { if (html) setPreviewHtml(html); })
      .catch(() => {})
      .finally(() => setPreviewLoading(false));
  }, [stage, showPreview]); // eslint-disable-line

  const handleSubmit = async () => {
    setSending(true);
    setResult(null);
    try {
      if (mode === 'now') {
        const res = await fetch(`${API_URL}/api/cfq/nurture/manual-send`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
          credentials: 'include',
          body: JSON.stringify({ stage, target: subscriber.email, override_stage: overrideStage }),
        });
        const d = await res.json();
        if (res.ok && d.sent > 0) {
          setResult({ ok: true, message: `Email ${stage} sent to ${subscriber.email}` });
          onSuccess?.();
        } else {
          setResult({ ok: false, message: d.detail || `Sent ${d.sent ?? 0}, failed ${d.failed ?? 1}` });
        }
      } else {
        const sendAtIso = new Date(scheduleAt).toISOString();
        const res = await fetch(`${API_URL}/api/cfq/nurture/schedule-send`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
          credentials: 'include',
          body: JSON.stringify({ stage, target: subscriber.email, send_at: sendAtIso, override_stage: overrideStage }),
        });
        const d = await res.json();
        if (res.ok) {
          setResult({ ok: true, message: `Email ${stage} scheduled for ${new Date(scheduleAt).toLocaleString('en-GB')}` });
          onSuccess?.();
        } else {
          setResult({ ok: false, message: d.detail || 'Failed to schedule' });
        }
      }
    } catch (e) {
      setResult({ ok: false, message: e.message });
    }
    setSending(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4,10,20,0.88)', backdropFilter: 'blur(8px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="subscriber-send-modal"
    >
      <div
        className="w-full rounded-2xl overflow-hidden flex flex-col"
        style={{
          maxWidth: showPreview ? '900px' : '480px',
          background: '#071B3A',
          border: '1px solid rgba(201,147,22,0.15)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-start justify-between" style={{ borderBottom: '1px solid rgba(250,248,243,0.06)' }}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] mb-0.5" style={{ color: '#C99316' }}>Send Nurture Email</p>
            <p className="text-cream font-medium text-sm">{subscriber.first_name ? `${subscriber.first_name} · ` : ''}{subscriber.email}</p>
            <p className="text-[11px] mt-0.5" style={{ color: 'rgba(250,248,243,0.3)' }}>
              Currently at stage {subscriber.nurture_stage ?? 0}
            </p>
          </div>
          <button onClick={onClose} className="text-cream/20 hover:text-cream/60 transition-colors mt-0.5" data-testid="subscriber-send-modal-close">
            <X size={17} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Controls */}
          <div className="p-5 space-y-5 flex-shrink-0" style={{ width: showPreview ? '320px' : '100%' }}>

            {/* Stage picker */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: 'rgba(250,248,243,0.35)' }}>
                Email to send
              </label>
              <select
                value={stage}
                onChange={e => setStage(Number(e.target.value))}
                className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                style={{ background: 'rgba(250,248,243,0.06)', border: '1px solid rgba(250,248,243,0.1)', color: 'rgba(250,248,243,0.85)' }}
                data-testid="send-stage-select"
              >
                {Object.entries(STAGE_SUBJECTS).map(([n, label]) => (
                  <option key={n} value={n}>{label}</option>
                ))}
              </select>
            </div>

            {/* Preview toggle */}
            <button
              onClick={() => setShowPreview(v => !v)}
              className="flex items-center gap-2 text-xs transition-colors"
              style={{ color: showPreview ? '#C99316' : 'rgba(250,248,243,0.35)' }}
              data-testid="toggle-preview-btn"
            >
              {showPreview ? <EyeOff size={13} /> : <Eye size={13} />}
              {showPreview ? 'Hide preview' : 'Preview this email'}
            </button>

            {/* Mode toggle */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: 'rgba(250,248,243,0.35)' }}>
                Delivery
              </label>
              <div className="flex gap-2">
                {[
                  { val: 'now', label: 'Send now', Icon: Send },
                  { val: 'schedule', label: 'Schedule', Icon: Calendar },
                ].map(({ val, label, Icon }) => (
                  <button
                    key={val}
                    onClick={() => setMode(val)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs transition-all"
                    style={{
                      background: mode === val ? 'rgba(201,147,22,0.14)' : 'rgba(250,248,243,0.04)',
                      border: `1px solid ${mode === val ? 'rgba(201,147,22,0.4)' : 'rgba(250,248,243,0.07)'}`,
                      color: mode === val ? '#C99316' : 'rgba(250,248,243,0.4)',
                    }}
                    data-testid={`mode-${val}`}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Schedule datetime */}
            {mode === 'schedule' && (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: 'rgba(250,248,243,0.35)' }}>
                  Send at (your local time)
                </label>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={e => setScheduleAt(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                  style={{
                    background: 'rgba(250,248,243,0.06)',
                    border: '1px solid rgba(250,248,243,0.1)',
                    color: 'rgba(250,248,243,0.85)',
                    colorScheme: 'dark',
                  }}
                  data-testid="schedule-datetime-input"
                />
              </div>
            )}

            {/* Override stage toggle */}
            <button
              onClick={() => setOverrideStage(v => !v)}
              className="flex items-center gap-2.5 text-xs w-full py-2 px-3 rounded-lg transition-all"
              style={{
                background: overrideStage ? 'rgba(201,147,22,0.08)' : 'rgba(250,248,243,0.03)',
                border: `1px solid ${overrideStage ? 'rgba(201,147,22,0.3)' : 'rgba(250,248,243,0.06)'}`,
                color: overrideStage ? '#C99316' : 'rgba(250,248,243,0.3)',
              }}
              data-testid="override-stage-toggle"
            >
              <div className="w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0"
                style={{ borderColor: overrideStage ? '#C99316' : 'rgba(250,248,243,0.2)', background: overrideStage ? 'rgba(201,147,22,0.2)' : 'transparent' }}>
                {overrideStage && <div className="w-2 h-2 rounded-sm" style={{ background: '#C99316' }} />}
              </div>
              Advance subscriber's sequence counter after send
            </button>

            {/* Result */}
            {result && (
              <div
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs"
                style={{
                  background: result.ok ? 'rgba(74,222,128,0.07)' : 'rgba(248,113,113,0.07)',
                  border: `1px solid ${result.ok ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.2)'}`,
                  color: result.ok ? '#4ade80' : '#f87171',
                }}
                data-testid="send-result"
              >
                {result.ok ? <CheckCircle size={13} className="flex-shrink-0 mt-0.5" /> : <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />}
                {result.message}
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={sending || !!result?.ok}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
              style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.4)', color: '#C99316' }}
              data-testid="submit-send-btn"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin" /> {mode === 'now' ? 'Sending…' : 'Scheduling…'}</>
                : mode === 'now'
                  ? <><Send size={13} /> Send now</>
                  : <><Clock size={13} /> Schedule email</>
              }
            </button>

          </div>

          {/* Preview pane */}
          {showPreview && (
            <div className="flex-1 overflow-hidden" style={{ borderLeft: '1px solid rgba(250,248,243,0.06)' }}>
              {previewLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 size={20} className="animate-spin" style={{ color: 'rgba(201,147,22,0.4)' }} />
                </div>
              ) : previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  title={`Preview Email ${stage}`}
                  className="w-full h-full"
                  style={{ border: 'none' }}
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-xs" style={{ color: 'rgba(250,248,243,0.2)' }}>
                  No preview available
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
